import { useState, useEffect } from 'react';
import { HelpCircle, X, Lightbulb, ChevronRight } from 'lucide-react';

const STORAGE_PREFIX = '@mybank:tutorial-';

const PageTutorial = ({ pageId, title, steps = [] }) => {
  const storageKey = `${STORAGE_PREFIX}${pageId}`;

  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(storageKey) === 'dismissed';
    } catch {
      return false;
    }
  });

  const [visible, setVisible] = useState(!dismissed);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (visible) {
      const timer = setTimeout(() => setAnimateIn(true), 50);
      return () => clearTimeout(timer);
    }
    setAnimateIn(false);
  }, [visible]);

  const handleDismiss = () => {
    setAnimateIn(false);
    setTimeout(() => {
      setVisible(false);
      setDismissed(true);
      try {
        localStorage.setItem(storageKey, 'dismissed');
      } catch {}
    }, 200);
  };

  const handleReopen = () => {
    setDismissed(false);
    setVisible(true);
    try {
      localStorage.removeItem(storageKey);
    } catch {}
  };

  if (!steps.length) return null;

  // Collapsed: show help button
  if (!visible) {
    return (
      <div className="flex justify-end">
        <button
          onClick={handleReopen}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-full transition-colors"
          title="Mostrar tutorial"
        >
          <HelpCircle size={14} />
          Ajuda
        </button>
      </div>
    );
  }

  // Expanded: tutorial card
  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50 via-white to-blue-50 dark:from-primary-900/20 dark:via-gray-800 dark:to-blue-900/20 shadow-sm transition-all duration-300 ${
        animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
      }`}
    >
      {/* Gradient accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary-500 via-blue-500 to-primary-400" />

      <div className="p-5 pt-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
              <Lightbulb size={18} className="text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                {title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {steps.length} dica{steps.length !== 1 ? 's' : ''} para voce
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            title="Fechar tutorial"
          >
            <X size={16} />
          </button>
        </div>

        {/* Steps */}
        <div className="space-y-2 mb-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-start gap-2.5 group"
            >
              <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary-500 dark:bg-primary-600 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">
                {index + 1}
              </span>
              <div className="flex items-start gap-1.5 flex-1 min-w-0">
                {step.icon && (
                  <span className="flex-shrink-0 mt-0.5 text-primary-500 dark:text-primary-400">
                    {step.icon}
                  </span>
                )}
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  {typeof step === 'string' ? step : step.text}
                </p>
              </div>
              <ChevronRight
                size={14}
                className="flex-shrink-0 mt-1 text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          ))}
        </div>

        {/* Dismiss button */}
        <div className="flex justify-end">
          <button
            onClick={handleDismiss}
            className="px-4 py-1.5 text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-100 dark:bg-primary-900/30 hover:bg-primary-200 dark:hover:bg-primary-900/50 rounded-lg transition-colors"
          >
            Entendi!
          </button>
        </div>
      </div>
    </div>
  );
};

export default PageTutorial;
